import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { FCC, useToast } from '@eluve/components';
import { FeatureFlagsEnum } from '@eluve/graphql-types';
import { FeatureFlagKeys, TenantOrUserFeatureFlagKeys } from '@eluve/posthog';
import {
  useAnalytics,
  useIsPostHogFeatureFlagEnabled,
} from '@eluve/posthog-react';

import {
  useIsFeatureFlagEnabled,
  useIsTenantOrUserFeatureFlagEnabled,
} from './useIsFeatureFlagEnabled';

/**
 * Renders children only if the supplied feature flag is enabled
 * for the user's tenant. Otherwise, returns null
 */
export const FeatureFlaggedComponent: FCC<{
  flag: FeatureFlagsEnum;
}> = ({ children, flag }) => {
  const isEnabled = useIsFeatureFlagEnabled(flag);

  if (!isEnabled) {
    return null;
  }

  return children;
};

export const UserFeatureFlaggedComponent: FCC<{
  flag: FeatureFlagKeys;
}> = ({ children, flag }) => {
  const analytics = useAnalytics();

  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    let mounted = true;

    const checkFlag = async () => {
      const isFeatureEnabled = await analytics.isFeatureEnabled(flag);
      if (mounted) {
        setIsEnabled(Boolean(isFeatureEnabled));
      }
    };

    checkFlag();

    return () => {
      mounted = false;
    };
  }, [analytics, flag]);

  // TODO(jesse)[ELU-2222]: Consider using <PostHogFeature> instead of a custom component
  // for more enriched analytics
  // https://posthog.com/docs/libraries/js

  useEffect(() => {
    if (isEnabled) {
      analytics.capture('$feature_view', { feature_flag: flag });
    }
  }, [isEnabled, analytics, flag]);

  if (!isEnabled) {
    return null;
  }

  return children;
};

/*
 * Renders children only if the supplied feature flag is enabled either for tenant or user
 * Else returns null
 */
export const TenantOrUserFeatureFlaggedComponent: FCC<{
  flag: TenantOrUserFeatureFlagKeys;
}> = ({ children, flag }) => {
  const isEnabled = useIsTenantOrUserFeatureFlagEnabled(flag);

  if (!isEnabled) {
    return null;
  }

  return children;
};

/**
 * Redirects the user to an alternative location in the app if they try
 * and access a feature flagged page which is not enabled for their tenant
 */
export const FeatureFlaggedPage: FCC<{
  flag: FeatureFlagsEnum;
  redirectTo?: string;
}> = ({ children, flag, redirectTo = '/' }) => {
  const toast = useToast();
  const isEnabled = useIsFeatureFlagEnabled(flag);

  if (!isEnabled) {
    toast.message('You have been redirected');

    return <Navigate to={redirectTo} />;
  }

  return children;
};

export const PostHogFeatureFlaggedPage: FCC<{
  flag: FeatureFlagKeys;
  redirectTo?: string;
}> = ({ flag, children, redirectTo = '/' }) => {
  const toast = useToast();
  const isEnabled = useIsPostHogFeatureFlagEnabled(flag);

  if (!isEnabled) {
    toast.message('You have been redirected');

    return <Navigate to={redirectTo} />;
  }

  return children;
};
